import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import Radio from '@mui/material/Radio'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LinkIcon from '@mui/icons-material/Link'
import Autocomplete from '@mui/material/Autocomplete'
import MuiTextField from '@mui/material/TextField'

import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import Modal from '@/v2/components/common/Modal'
import Select from '@/v2/components/common/Select'

import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useFindPatient } from '@/v2/hooks/patient/useFindPatient'
import {
  useFamilyMembers,
  useCreateFamilyMember,
  useUpdateFamilyMember,
  useDeleteFamilyMember,
  RELATIONSHIP_OPTIONS,
} from '@/v2/hooks/patient/useFamilyMembers'
import { formatSearch } from '@/v2/utils/format'

const AddRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  margin-bottom: 24px;
`

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
`

const MemberRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #eeeded;

  &:last-of-type {
    border-bottom: 1px solid #eeeded;
  }
`

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const NameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const TrashButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b6b6b;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #c62828;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const RELATIONSHIP_LABELS = RELATIONSHIP_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {})

const formatRelationship = value =>
  RELATIONSHIP_LABELS[value] || value || 'Family member'

const FamilyMembersModal = ({ isOpen, onClose, patientId, fullScreen }) => {
  const theme = useTheme()
  const { onOpenSnackbar, onCloseSnackbar } = useSnackbarContext()

  const { data: members } = useFamilyMembers(patientId)
  const {
    mutateAsync: createMember,
    isPending: isCreating,
  } = useCreateFamilyMember(patientId)
  const {
    mutateAsync: updateMember,
    isPending: isUpdating,
  } = useUpdateFamilyMember(patientId)
  const {
    mutateAsync: deleteMember,
    isPending: isDeleting,
  } = useDeleteFamilyMember(patientId)

  const [searchInput, setSearchInput] = useState('')
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [newRelationship, setNewRelationship] = useState('')
  const [pendingPrimaryId, setPendingPrimaryId] = useState(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setSearchInput('')
      setSelectedPatient(null)
      setSearchTerm('')
      setNewRelationship('')
      setPendingPrimaryId(null)
      onCloseSnackbar()
    }
  }, [isOpen, onCloseSnackbar])

  const currentMainId = members.find(m => m.main)?.id ?? null
  const effectivePrimaryId =
    pendingPrimaryId !== null ? pendingPrimaryId : currentMainId
  const hasPrimaryChange =
    pendingPrimaryId !== null && pendingPrimaryId !== currentMainId

  const { patients = [], isLoading: isSearchingPatients } = useFindPatient(
    searchTerm
  )

  const isSaving = isCreating || isUpdating || isDeleting

  const searchOptions = useMemo(
    () => patients.filter(p => p.id !== patientId),
    [patients, patientId]
  )

  const resetAddForm = () => {
    setSearchInput('')
    setSelectedPatient(null)
    setSearchTerm('')
    setNewRelationship('')
  }

  const close = () => {
    resetAddForm()
    setPendingPrimaryId(null)
    onClose()
  }

  const requestClose = () => {
    if (
      hasPrimaryChange &&
      !window.confirm('Are you sure you want to leave without saving changes?')
    ) {
      return
    }
    close()
  }

  const handleSaveChanges = async () => {
    if (hasPrimaryChange) {
      try {
        await updateMember({ id: pendingPrimaryId, main: true })
        onOpenSnackbar('Primary contact updated', 'success')
      } catch (err) {
        onOpenSnackbar(
          err.message || 'Failed to update primary contact',
          'error'
        )
        return
      }
    }
    close()
  }

  const handleSearchInputChange = (_event, value, reason) => {
    if (reason === 'reset') return
    setSearchInput(value)
    if (selectedPatient) setSelectedPatient(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchTerm(formatSearch(value))
    }, 300)
  }

  const handleSearchSelect = (_event, value) => {
    if (value && typeof value === 'object') {
      setSelectedPatient(value)
      setSearchInput(value.fullName || '')
    } else {
      setSelectedPatient(null)
    }
  }

  const handleAddMember = async () => {
    if (!newRelationship) return

    if (selectedPatient) {
      try {
        await createMember({
          relatedPatientId: selectedPatient.id,
          relationship: newRelationship,
        })
        resetAddForm()
        onOpenSnackbar('Family member added', 'success')
      } catch (err) {
        onOpenSnackbar(err.message || 'Failed to add family member', 'error')
      }
      return
    }

    const fullName = searchInput.trim()
    if (!fullName) return
    try {
      await createMember({ fullName, relationship: newRelationship })
      resetAddForm()
      onOpenSnackbar('Family member added', 'success')
    } catch (err) {
      onOpenSnackbar(err.message || 'Failed to add family member', 'error')
    }
  }

  const handleSetPrimary = member => {
    setPendingPrimaryId(member.id)
  }

  const handleDelete = async member => {
    const wasMain = !!member.main
    const hasOthers = (members?.length ?? 0) > 1
    const confirmMessage = wasMain
      ? `Remove ${member.fullName} from family members? They are the current primary contact — you'll need to designate a new primary afterward.`
      : `Remove ${member.fullName} from family members?`
    if (!window.confirm(confirmMessage)) return
    try {
      const result = await deleteMember(member.id)
      const baseMessage = result?.message || 'Family member removed'
      const message =
        wasMain && hasOthers
          ? `${baseMessage}. Select a new primary contact.`
          : baseMessage
      onOpenSnackbar(message, 'success')
    } catch (err) {
      onOpenSnackbar(err.message || 'Failed to remove family member', 'error')
    }
  }

  const canAdd =
    newRelationship && !isSaving && (!!selectedPatient || !!searchInput.trim())

  return (
    <Modal
      isOpen={isOpen}
      title="Family Members"
      onClose={requestClose}
      onConfirm={handleSaveChanges}
      confirmLabel="Save Changes"
      showConfirm={hasPrimaryChange}
      showClose={false}
      submitDisabled={isSaving}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <AddRow>
        <Autocomplete
          freeSolo
          fullWidth
          autoComplete
          includeInputInList
          filterOptions={x => x}
          options={searchOptions}
          value={selectedPatient}
          inputValue={searchInput}
          onInputChange={handleSearchInputChange}
          onChange={handleSearchSelect}
          loading={!!searchTerm && isSearchingPatients}
          noOptionsText={
            searchTerm ? 'No patients found' : 'Type to search patients'
          }
          getOptionLabel={option => {
            if (typeof option === 'string') return option
            return option?.fullName || ''
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ display: 'block' }}>
              <Typography variant="h4">{option.fullName}</Typography>
              {(option.phoneNumber || option.email) && (
                <Typography
                  variant="h5"
                  sx={{ color: theme.palette.gray.dark }}
                >
                  {[option.phoneNumber, option.email]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </li>
          )}
          renderInput={params => (
            <MuiTextField
              {...params}
              placeholder="Search for an existing patient or type a name"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: theme.palette.gray.dark }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Select
          fullWidth
          value={newRelationship}
          onChange={setNewRelationship}
          options={RELATIONSHIP_OPTIONS}
          placeholder="Select a relationship"
        />
        <Button
          variant="outlined"
          onClick={handleAddMember}
          disabled={!canAdd}
          sx={{
            alignSelf: 'flex-start',
            color: theme.palette.black.main,
            borderColor: '#d0d0d0',
            textTransform: 'none',
            fontWeight: 400,
            marginLeft: '-2px',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: 'transparent',
            },
          }}
        >
          Add Member
        </Button>
      </AddRow>

      <MemberList>
        {members.map(member => (
          <MemberRow key={member.id}>
            <MemberInfo>
              <NameRow>
                <Typography bold fontSize="15px">
                  {member.fullName}
                </Typography>
                {member.relatedPatientId && (
                  <LinkIcon
                    fontSize="small"
                    sx={{ color: theme.palette.gray.dark }}
                  />
                )}
              </NameRow>
              <Typography
                fontSize="14px"
                sx={{ color: theme.palette.gray.dark }}
              >
                {formatRelationship(member.relationship)}
              </Typography>
            </MemberInfo>
            <FormControlLabel
              control={
                <Radio
                  checked={effectivePrimaryId === member.id}
                  onClick={() => handleSetPrimary(member)}
                  size="small"
                  sx={{
                    color: theme.palette.secondary.main,
                    '&.Mui-checked': {
                      color: theme.palette.secondary.main,
                    },
                  }}
                />
              }
              label={<Typography fontSize="14px">Primary</Typography>}
              sx={{ marginLeft: 0, marginRight: 0, gap: '6px' }}
            />
            <TrashButton
              type="button"
              aria-label={`Remove ${member.fullName}`}
              onClick={() => handleDelete(member)}
              disabled={isSaving}
            >
              <DeleteOutlineIcon sx={{ fontSize: '24px' }} />
            </TrashButton>
          </MemberRow>
        ))}
      </MemberList>
    </Modal>
  )
}

export default FamilyMembersModal
